import { colors } from "@indeed/ifl-tokens";
import React, { useCallback, useRef, useState } from "react";
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { AccessibilityInfo, findNodeHandle, type View, type ViewStyle } from "react-native";

import { Box, Button, ChevronDown, ChevronUp, Text } from "../../../../../mads";
import { LinearGradient } from "../../../../../shared/components/LinearGradient/linear-gradient";
import { useI18n } from "../../../../../shared/I18n";

const fadeStyle: ViewStyle = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: 50,
};

function VerticalFade() {
  return (
    <LinearGradient
      colors={["rgba(255, 255, 255, 0.0)", "rgba(255, 255, 255, 1.0)"]}
      start={{ x: 0, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={fadeStyle}
      pointerEvents="none"
    />
  );
}

export type HiddenPreviewProps = {
  /**
   * The content to render. A single node that stays mounted across expand/collapse —
   * callers control what it shows per state (e.g. a truncated vs. full body) so the
   * underlying view is never remounted. A fade overlays it while collapsed.
   */
  children: React.ReactNode;
  /**
   * Accessibility label applied to the content wrapper. Callers should keep this in
   * sync with what `children` renders in the current state so the screen-reader and
   * visual experiences match. When omitted, the wrapper is a plain non-accessible Box.
   */
  accessibilityLabel?: string;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  showMoreLabel?: string;
  showLessLabel?: string;
};

export function HiddenPreview({
  children,
  accessibilityLabel,
  isExpanded: controlledExpanded,
  onExpandedChange,
  showMoreLabel,
  showLessLabel,
}: HiddenPreviewProps) {
  const { gettext } = useI18n();
  const [internalExpanded, setInternalExpanded] = useState(false);
  const contentRef = useRef<View>(null);
  const hasFocusedRef = useRef(false);

  const resolvedShowMoreLabel = showMoreLabel ?? gettext("Show more");
  const resolvedShowLessLabel = showLessLabel ?? gettext("Show less");

  const isExpanded = controlledExpanded ?? internalExpanded;

  const handleToggle = useCallback(() => {
    const next = !isExpanded;
    if (!next) {
      hasFocusedRef.current = false;
    }
    if (onExpandedChange) {
      onExpandedChange(next);
    } else {
      setInternalExpanded(next);
    }
  }, [isExpanded, onExpandedChange]);

  // Move screen-reader focus onto the content only when it becomes expanded.
  const handleContentLayout = useCallback(() => {
    if (isExpanded && !hasFocusedRef.current && contentRef.current) {
      const node = findNodeHandle(contentRef.current);
      if (node) {
        hasFocusedRef.current = true;
        AccessibilityInfo.setAccessibilityFocus(node);
      }
    }
  }, [isExpanded]);

  return (
    <Box>
      <Box sx={{ position: "relative" }}>
        <Box
          ref={contentRef}
          accessible={accessibilityLabel !== undefined ? true : undefined}
          accessibilityLabel={accessibilityLabel}
          onLayout={handleContentLayout}
        >
          {/* With a label, hide the subtree from TalkBack so only the label is
              announced; without one, keep descendants accessible. */}
          <Box
            importantForAccessibility={
              accessibilityLabel !== undefined ? "no-hide-descendants" : undefined
            }
          >
            {children}
          </Box>
        </Box>
        {!isExpanded ? <VerticalFade /> : null}
      </Box>

      <Button
        variant="text"
        iconAfter={
          isExpanded ? (
            <ChevronUp color={colors.foreground.primary["[base]"]} />
          ) : (
            <ChevronDown color={colors.foreground.primary["[base]"]} />
          )
        }
        onPress={handleToggle}
        ariaLabel={isExpanded ? resolvedShowLessLabel : resolvedShowMoreLabel}
      >
        <Text>{isExpanded ? resolvedShowLessLabel : resolvedShowMoreLabel}</Text>
      </Button>
    </Box>
  );
}
